import objectHash from 'object-hash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { Loader } from '@/components/Loader';
import { Typography } from '@/components/Typography';
import type {
  OrderAppCtaType,
  OrderAppProductVariantsBusinessDetailsQuery,
} from '@/graphql/orderApp/api';
import type { ShoppingBagStrapsModalFragment } from '@/graphql/saleor/api';
import { useStrapSizesQuery } from '@/graphql/saleor/api';
import type {
  AllSelectedProducts,
  Product,
} from '@/hooks/useAllSelectedProductsState';
import { useBucklesAndTools } from '@/hooks/useBucklesAndTools';
import { useCheckout } from '@/hooks/useCheckout';
import { useWristSize } from '@/hooks/useWristSize';
import { findProductCtaFromBusinessData } from '@/integrations/orderApp';
import { getMultiselectValue } from '@/lib/attributes';
import { STRAP_SIZES_FETCH_LIMIT } from '@/lib/const';
import { mapEdgesToItems } from '@/lib/maps';
import { isToolInShoppingBag } from '@/lib/product';
import type { Maybe, SetState } from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { useRegionProvider } from '@/providers/RegionProvider';
import { defined as ShoppingBagStrapDetailsMessages } from '@/views/ShoppingBagView/components/ShoppingBagStrapDrawer/components/ShoppingBagStrapDetails/messages';
import { SizesModal } from '@/views/StrapView/components/Sizes/components';

import { ProductItem } from '../ProductItem';

import * as M from './messages';
import * as S from './ShoppingBagStrapDetails.styles';

export interface ShoppingBagStrapDetailsProps {
  allSelectedProducts: AllSelectedProducts;
  orderAppData: Maybe<OrderAppProductVariantsBusinessDetailsQuery>;
  orderCtaType?: OrderAppCtaType;
  selectedStrapSku: string | null;
  setAllSelectedProducts: SetState<AllSelectedProducts>;
  strap: ShoppingBagStrapsModalFragment;
  updateSelectedStrapSku: (sku: string) => void;
}

export const ShoppingBagStrapDetails = ({
  allSelectedProducts,
  orderAppData,
  orderCtaType,
  selectedStrapSku,
  setAllSelectedProducts,
  strap,
  updateSelectedStrapSku,
}: ShoppingBagStrapDetailsProps) => {
  const { formatMessage } = useIntl();
  const { query, unit } = useRegionProvider();
  const {
    snapshot: { checkout },
  } = useCheckout();

  const [isSizesModalOpen, setIsSizesModalOpen] = useState(false);
  const [selectedBuckleSku, setSelectedBuckleSku] = useState<null | string>(
    null,
  );
  const [selectedToolSku, setSelectedToolSku] = useState<null | string>(null);

  const { data } = useStrapSizesQuery({
    variables: {
      ...query,
      filter: {
        sku: [strap.sku!, ...getMultiselectValue(strap.product.otherSizesSkus)],
      },
      limit: STRAP_SIZES_FETCH_LIMIT,
    },
    fetchPolicy: 'no-cache',
  });

  const { buckles, loading, tools } = useBucklesAndTools(
    strap.product.compatibleBuckles,
    orderCtaType,
  );

  const availableSizesProducts = mapEdgesToItems(data?.productVariants).filter(
    (item) => {
      const productCTA = orderAppData?.productVariants?.find(
        (jtem) => jtem.sku === item.sku,
      );

      return productCTA?.variantCTA === orderCtaType;
    },
  );

  const updateProductsWithNewStrap = (
    currentProducts: Product[],
    newStrapId: Product['id'],
    sku: Product['sku'],
  ): Product[] => {
    const oldStrap = currentProducts.find(
      (product) => product.productTypeSlug === 'strap',
    ) as Product;
    const filteredProducts = currentProducts.filter(
      (product) => product.productTypeSlug !== 'strap',
    );

    return [
      ...filteredProducts,
      {
        id: newStrapId,
        price: oldStrap.price,
        productTypeSlug: 'strap',
        sku: sku,
      },
    ];
  };

  const hash = objectHash(data || {});
  const availableBuckles = buckles ?? [];
  const currentStrapSKU = allSelectedProducts.products.find(
    (product) => product.productTypeSlug === 'strap',
  )?.sku;

  useEffect(() => {
    if (
      findProductCtaFromBusinessData(orderAppData, currentStrapSKU) !==
      orderCtaType
    ) {
      const newStrap = data?.productVariants?.edges.find((item) => {
        return (
          findProductCtaFromBusinessData(orderAppData, item.node.sku) ===
          orderCtaType
        );
      });
      if (newStrap) {
        setAllSelectedProducts((curr) => {
          return {
            products: updateProductsWithNewStrap(
              curr.products,
              newStrap.node.id,
              newStrap.node.sku,
            ),
            totalPrice: { ...curr.totalPrice },
          } satisfies AllSelectedProducts;
        });
      }
    }
  }, [hash, orderCtaType]);

  const currentSizeName = availableSizesProducts.find(
    (e) => e.sku === selectedStrapSku,
  )?.product?.currentSize?.values[0].name;
  const wristInterval = useWristSize(currentSizeName, unit);

  return (
    <>
      <S.SmallHeader
        colorized={false}
        data-testid='ShoppingBagStrapDetailsSmallHeader'
        text={formatMessage(M.defined.availableSize)}
      />
      <S.AvailableSizesRow>
        {availableSizesProducts.map((availableSizeProduct, index) => (
          <S.Size
            key={availableSizeProduct.id}
            data-testid={`ShoppingBagStrapDetailsSize-${index}`}
            isSelected={
              availableSizeProduct.id ===
                allSelectedProducts.products.find(
                  (product) => product.productTypeSlug === 'strap',
                )?.id || selectedStrapSku === availableSizeProduct.sku
            }
            onClick={() => {
              setAllSelectedProducts(
                (curr) =>
                  ({
                    products: updateProductsWithNewStrap(
                      curr.products,
                      availableSizeProduct.id,
                      availableSizeProduct.sku,
                    ),
                    totalPrice: { ...curr.totalPrice },
                  } satisfies AllSelectedProducts),
              );
              updateSelectedStrapSku(availableSizeProduct.sku as string);
            }}
          >
            {getMultiselectValue(availableSizeProduct.product.currentSize)}
          </S.Size>
        ))}
        <S.Ruler
          data-testid='ShoppingBagStrapDetailsRuler'
          fill='#C4C4C4'
          onClick={() => setIsSizesModalOpen(true)}
        />
        <SizesModal isOpen={isSizesModalOpen} setIsOpen={setIsSizesModalOpen} />
      </S.AvailableSizesRow>
      <S.SizeInfoText data-testid='ShoppingBagStrapDetailsSizeInfoText'>
        {M.MeasuringText(wristInterval.min, wristInterval.max, unit)}
      </S.SizeInfoText>
      {loading ? (
        <Loader
          data-testid='ShoppingBagStrapDetailsLoader'
          height='fullHeight'
        />
      ) : (
        <>
          <S.Divider data-testid='ShoppingBagStrapDetailsDivider' />
          {availableBuckles?.length ? (
            <>
              <S.SmallHeader
                colorized={false}
                data-testid='ShoppingBagStrapDetailsCompatibleBuckleHeader'
                text={formatMessage(M.defined.addACompatibleBuckle)}
              />
              <S.BucklesWrapper $nbBuckles={availableBuckles.length}>
                {availableBuckles.map((buckle, index) => (
                  <ProductItem
                    key={buckle.sku}
                    data={buckle}
                    data-test-id={`ShoppingBagStrapDetailsProductItem-${index}`}
                    isClickable
                    nbBuckles={availableBuckles.length}
                    productType='buckle'
                    selectedSku={selectedBuckleSku}
                    setAllSelectedProducts={setAllSelectedProducts}
                    setSelectedSku={setSelectedBuckleSku}
                  />
                ))}
              </S.BucklesWrapper>
            </>
          ) : (
            <Flex alignItems='center'>
              <Typography data-testid='ShoppingBagStrapDetailsBucklesMessage'>
                {formatMessage(
                  ShoppingBagStrapDetailsMessages.buckleOutOfStock,
                )}
              </Typography>
            </Flex>
          )}
          {!isToolInShoppingBag(checkout) && tools?.length && (
            <Box>
              <S.NiceToHaveHeader
                colorized={false}
                data-testid='ShoppingBagStrapDetailsNiceToHaveHeader'
                text={formatMessage(commonMessages.niceToHave)}
              />
              <S.NiceToHaveText>{M.NiceToHavetext}</S.NiceToHaveText>
              {tools?.map((tool) => (
                <ProductItem
                  key={tool.sku}
                  data={tool}
                  isClickable
                  productType='tool'
                  selectedSku={selectedToolSku}
                  setAllSelectedProducts={setAllSelectedProducts}
                  setSelectedSku={setSelectedToolSku}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </>
  );
};
